import { useState, useMemo, useEffect } from 'react';

import { resetPassword } from 'aws-amplify/auth';
import { Trans, useTranslation } from 'react-i18next';

import CustomButton from '../../../../shared/components/Buttons/CustomButton/CustomButton';
import IconButton from '../../../../shared/components/Buttons/IconButton/IconButton';
import BdsTextButton from '../../../../shared/components/Buttons/TextButton/TextButton';
import Toast from '../../../../shared/components/Globals/Toast/Toast';
import CustomInput from '../../../../shared/components/Inputs/CustomInput/CustomInput';
import { ACCESS_INSTRUCTIONS_PAGE, MODE_CONFIG, SEND_CODE_COUNTDOWN } from '../../../../shared/constants/auth';
import { ARROW_LEFT_ICON, TRASH_ICON } from '../../../../shared/constants/images';
import {
  CLICK_ACTION,
  CONTINUE_CODE_VERIFICATION,
  RESEND_CODE_VERIFICATION,
  SECTION_CREATE_ACCOUNT,
  SECTION_FORGOT_PASSWORD
} from '../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../shared/constants/toast';
import { IVerificationCode } from '../../../../shared/interfaces/auth';
import Users from '../../../../shared/services/api/users/users';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_AUTH } from '../../../../shared/translations/auth';

import './VerificationCode.scss';

const VerificationCode = ({ codeType, email, loading, hasError, handleNext, handleGoBack }: IVerificationCode) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [counter, setCounter] = useState<number>(SEND_CODE_COUNTDOWN);
  const [loadingResend, setLoadingResend] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const config = useMemo(() => MODE_CONFIG[codeType], [codeType]);

  const codeFormatter = (value: string): string => {
    const filtered = value
      .split('')
      .filter((char) => config.pattern.test(char))
      .join('');
    return filtered.slice(0, config.length);
  };

  const clearCode = () => {
    setCode('');
    setErrorMessage('');
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = codeFormatter(e.target.value);
    setCode(formatted);
    setErrorMessage('');
  };

  const handleBlur = () => {
    if (code.length > 0 && code.length !== config.length) {
      setErrorMessage(t(MODE_CONFIG.errorMessage));
    }
    if (codeType === 'password-reset' && isCodeValid()) {
      handleNextStep();
    }
  };

  const isCodeValid = (): boolean => {
    return code.length === config.length && config.pattern.test(code);
  };

  const handleNextStep = () => {
    if (!isCodeValid()) {
      setErrorMessage(t(MODE_CONFIG.errorMessage));
      return;
    }
    const gtmParams = codeType === 'create-account' ? SECTION_CREATE_ACCOUNT : SECTION_FORGOT_PASSWORD;
    TagManagerService.pushEvent(CLICK_ACTION, CONTINUE_CODE_VERIFICATION, gtmParams);
    handleNext(code);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNextStep();
    }
  };

  const redirectToHelpCenter = () => {
    window.open(ACCESS_INSTRUCTIONS_PAGE, '_blank', 'noopener');
  };

  const handleResendCode = async () => {
    if (counter > 0) return;
    try {
      setLoadingResend(true);
      setCounter(SEND_CODE_COUNTDOWN);
      if (codeType === 'password-reset') {
        TagManagerService.pushEvent(CLICK_ACTION, RESEND_CODE_VERIFICATION, SECTION_FORGOT_PASSWORD);
        await resetPassword({ username: email });
      } else if (codeType === 'create-account') {
        TagManagerService.pushEvent(CLICK_ACTION, RESEND_CODE_VERIFICATION, SECTION_CREATE_ACCOUNT);
        await Users.resendCode(email);
      }

      setCode('');
      setErrorMessage('');
    } catch {
      setOpenToast(true);
    } finally {
      setLoadingResend(false);
    }
  };

  useEffect(() => {
    if (counter <= 0) {
      return;
    }
    const timeoutId = setTimeout(() => {
      setCounter((currentCounter) => currentCounter - 1);
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [counter]);

  const getButtonText = () => {
    if (isCodeValid()) {
      return t(TRANSLATIONS_AUTH.verificationCode.buttons.continue);
    }
    if (counter > 0) {
      const minutes = Math.floor(counter / 60)
        .toString()
        .padStart(2, '0');
      const seconds = (counter % 60).toString().padStart(2, '0');
      return t(TRANSLATIONS_AUTH.verificationCode.buttons.disabled, { param1: minutes, param2: seconds });
    }
    return t(TRANSLATIONS_AUTH.verificationCode.buttons.enabled);
  };

  useEffect(() => {
    if (hasError) {
      clearCode();
    }
  }, [hasError]);

  const isButtonDisabled = !isCodeValid() && counter > 0;

  return (
    <div className="auth__card">
      <div className="auth__go-back">
        <BdsTextButton action={() => handleGoBack(-1)} iconLeft={ARROW_LEFT_ICON} variant="primary">
          {t(TRANSLATIONS_AUTH.goBack)}
        </BdsTextButton>
      </div>
      <div className="flex flex-col verification-code">
        <div className="flex flex-col text-left verification-code__header">
          <div className="verification-code__title">{t(TRANSLATIONS_AUTH.verificationCode.title)}</div>
          <div className="verification-code__description">{t(TRANSLATIONS_AUTH.verificationCode.description)}</div>
        </div>
        <CustomInput
          id="verifyCode"
          name="verifyCode"
          placeholder={t(TRANSLATIONS_AUTH.verificationCode.codeInput.placeholder)}
          value={code}
          onChange={handleCodeChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          maxLength={config.length}
          error={errorMessage}
          suffix={
            codeType === 'create-account' && code ? (
              <IconButton icon={TRASH_ICON} action={clearCode} variant="transparent" size="s" label="Clear code" />
            ) : undefined
          }
          containerClassName="auth__input"
        />
        <div className="flex flex-row items-center verification-code__actions">
          <CustomButton
            variant="primary"
            size="l"
            onClick={isCodeValid() ? handleNextStep : handleResendCode}
            disabled={isButtonDisabled || loading || loadingResend}
            loading={loadingResend || loading}
            className="verification-code__button"
          >
            {getButtonText()}
          </CustomButton>
          {codeType === 'create-account' && (
            <div className="flex flex-col verification-code__help-center">
              <Trans
                i18nKey={TRANSLATIONS_AUTH.verificationCode.buttons.helpcenter}
                components={{
                  u: (
                    <button
                      className="border-0 outline-none bg-transparent cursor-pointer verification-code__help-link"
                      onClick={redirectToHelpCenter}
                    />
                  )
                }}
              />
            </div>
          )}
        </div>
      </div>
      <Toast
        message={t(GENERIC_ERROR_TOAST.message)}
        variant={GENERIC_ERROR_TOAST.variant}
        open={openToast}
        onClose={() => setOpenToast(false)}
      />
    </div>
  );
};

export default VerificationCode;
